// src/components/modals/DeleteSMSCampaignModal.jsx
import { useState } from 'react'
import { X, Trash2, AlertTriangle, MessageCircle, Users } from 'lucide-react'
import { Button } from '../ui/Button'
import ButtonSpinner from '../ui/ButtonSpinner'
import SMSCampaignService from '../../services/smsCampaignService'
import { SMSCampaignModel } from '../../utils/smsCampaignModel'

const DeleteSMSCampaignModal = ({ isOpen, onClose, onSuccess, campaign }) => {
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState('')

  if (!isOpen || !campaign) return null

  const handleDelete = async () => {
    if (isDeleting) return

    try {
      setIsDeleting(true)
      setError('')

      // Check if campaign can be deleted
      if (!SMSCampaignModel.canDelete(campaign.status)) {
        setError('This campaign cannot be deleted in its current status')
        return
      }

      console.log('📱 Deleting SMS campaign:', campaign.id)
      const response = await SMSCampaignService.deleteSMSCampaign(campaign.id)

      if (response.success) {
        // Close modal and trigger success callback
        onSuccess()
      } else {
        setError(response.message || 'Failed to delete SMS campaign')
      }

    } catch (error) {
      console.error('Failed to delete SMS campaign:', error)
      setError(error.message || 'Failed to delete SMS campaign')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleClose = () => {
    if (!isDeleting) {
      setError('')
      onClose()
    }
  }

  const getDeletionWarning = () => {
    switch (campaign.status) {
      case 'running':
        return {
          level: 'error',
          message: 'This campaign is currently running and cannot be deleted. Please wait for it to complete or fail before attempting deletion.',
          canDelete: false
        }
      case 'completed':
        return {
          level: 'warning',
          message: 'This campaign has been completed successfully. Deleting it will remove all campaign data and logs permanently.',
          canDelete: true
        }
      case 'failed':
        return {
          level: 'warning', 
          message: 'This campaign failed during execution. You may want to retry it instead of deleting. Deletion will remove all campaign data permanently.',
          canDelete: true
        }
      case 'draft':
        return {
          level: 'info',
          message: 'This campaign is in draft status and has not been sent. Deletion will remove the campaign permanently.',
          canDelete: true
        }
      default:
        return {
          level: 'warning',
          message: 'Deletion will permanently remove this campaign and all associated data.',
          canDelete: true
        }
    }
  }

  const warning = getDeletionWarning()

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-full">
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Delete SMS Campaign</h2>
              <p className="text-sm text-gray-600">This action cannot be undone</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isDeleting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Error Alert */}
          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Campaign Info */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-gray-100 rounded-full">
                <MessageCircle className="h-4 w-4 text-gray-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900">{campaign.title}</h4>
                <div className="mt-2 space-y-1 text-xs text-gray-600">
                  <div>ID: {campaign.id}</div>
                  <div className="flex items-center">
                    Status: 
                    <span className={`ml-2 px-2 py-1 rounded text-xs ${campaign.statusConfig.color}`}>
                      {campaign.statusConfig.icon} {campaign.statusConfig.label}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Users className="h-3 w-3 mr-1" />
                    Target: {campaign.customerGroup.name} ({campaign.customerGroup.customerCount} customers)
                  </div>
                  <div>Created: {campaign.createdAtFormatted}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Warning Message */}
          <div className={`p-4 rounded-lg border-l-4 ${
            warning.level === 'error' ? 'bg-red-50 border-red-400' :
            warning.level === 'warning' ? 'bg-yellow-50 border-yellow-400' :
            'bg-blue-50 border-blue-400'
          }`}>
            <div className="flex items-start space-x-2">
              <AlertTriangle className={`h-5 w-5 mt-0.5 ${
                warning.level === 'error' ? 'text-red-600' :
                warning.level === 'warning' ? 'text-yellow-600' :
                'text-blue-600'
              }`} />
              <div>
                <h5 className={`text-sm font-medium ${
                  warning.level === 'error' ? 'text-red-800' :
                  warning.level === 'warning' ? 'text-yellow-800' :
                  'text-blue-800'
                }`}>
                  {warning.level === 'error' ? 'Cannot Delete' : 'Deletion Warning'}
                </h5>
                <p className={`text-sm mt-1 ${
                  warning.level === 'error' ? 'text-red-700' :
                  warning.level === 'warning' ? 'text-yellow-700' :
                  'text-blue-700'
                }`}>
                  {warning.message}
                </p>
              </div>
            </div>
          </div>

          {/* Consequences */}
          {warning.canDelete && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <h5 className="text-sm font-medium text-gray-700 mb-2">What will be deleted:</h5>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• Campaign information and settings</li>
                <li>• Associated SMS logs and delivery records</li>
                <li>• Campaign statistics and history</li>
                <li>• This action cannot be reversed</li>
              </ul>
            </div>
          )}

          {/* Confirmation */}
          {warning.canDelete && (
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Are you sure you want to delete this SMS campaign?</strong>
              </p>
              <p className="text-xs text-red-700 mt-1">
                Type the campaign title to confirm deletion: <strong>{campaign.title}</strong>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          
          {warning.canDelete && (
            <Button
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white min-w-[100px]"
            >
              {isDeleting ? <ButtonSpinner size="sm" /> : 'Delete Campaign'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default DeleteSMSCampaignModal